import { Divider, Stack } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { ActivitySchema } from 'store/config/schema';
import { SelectInput, TextInput } from 'components/Form';
import { Button, Typography } from 'components';

interface IAddClipForm {
  activities: ActivitySchema[];
  onSaved: (selectedActivity: string, newActivity: string) => void;
}

const AddActivityForm: React.FC<IAddClipForm> = ({ activities, onSaved }) => {
  const [newActivity, setNewActivity] = React.useState<string>('');
  const [selectedActivity, setSelectedActivity] = React.useState<string>('');
  const [inputError, setInputError] = React.useState<string>('');
  const [isDisabled, setDisabled] = useState(false);

  const validateActivityName = (value: string): string => {
    if (!value) {
      return 'Name cannot be empty';
    }

    if (value.length < 4) {
      return 'Minimum length of the name must be 4 characters';
    }

    if (value.length > 75) {
      return 'Maximum length of the name can be 75 characters';
    }

    const regex = /^[a-zA-Z0-9][a-zA-Z0-9_\- ]*$/;
    if (!regex.test(value) || value.trim() === '') {
      return 'Name must start with a letter or number and can only contain letters, numbers, hyphens, underscores, and spaces.';
    }

    return '';
  };

  const isValid = () => {
    if (activities.length > 0) {
      return (
        (Boolean(selectedActivity) || (Boolean(newActivity) && !inputError)) &&
        !isDisabled
      );
    }
    return Boolean(newActivity) && !inputError && !isDisabled;
  };

  useEffect(() => {
    const isDuplicate = activities.some(data => data?.activity === newActivity);
    setDisabled(isDuplicate);
    setInputError(validateActivityName(newActivity));
  }, [newActivity, activities]);

  const handleSave = (e: any) => {
    e.preventDefault();
    if (isValid()) {
      onSaved(selectedActivity, newActivity);
      setNewActivity('');
      setSelectedActivity('');
      setInputError('');
    }
  };

  const handleActivityChange = (value: string) => {
    setNewActivity(value);
    setInputError(validateActivityName(value));
  };

  useEffect(() => {
    setNewActivity('');
    setInputError('');
  }, [selectedActivity]);

  return (
    <div>
      <form onSubmit={handleSave}>
        <Stack spacing={2} alignContent={'center'}>
          {activities.length > 0 && (
            <>
              <SelectInput
                label="Select Activity"
                name="activity"
                options={[
                  { value: '', label: 'None' },
                  ...activities.map(activity => ({
                    value: activity.id,
                    label: activity.activity
                  }))
                ]}
                variant="outlined"
                value={selectedActivity}
                onChange={value => setSelectedActivity(value)}
              />
              <Divider />
              <Typography
                variant="subtitle1"
                color="textSecondary"
                textAlign={'center'}
              >
                OR
              </Typography>
            </>
          )}
          {isDisabled && (
            <Typography variant="subtitle1" color="error" textAlign={'center'}>
              Activity already exists, please select from above given options.
            </Typography>
          )}
          <TextInput
            onKeyDown={(event: any) => event.stopPropagation()}
            label="New Activity Name"
            name="activity"
            variant="outlined"
            size="small"
            disabled={Boolean(selectedActivity)}
            value={newActivity}
            onChange={handleActivityChange}
            error={inputError}
            helperText={`${newActivity.length}/75 characters`}
          />
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            disabled={!isValid()}
          >
            Save
          </Button>
        </Stack>
      </form>
    </div>
  );
};

export default AddActivityForm;
